import type { ChartRow, ChartSeries, ChartSpec } from './types';

type LegacyPoint = {
  time?: string | number;
  value?: number;
  open?: number;
  high?: number;
  low?: number;
  close?: number;
};

type LegacySeries = {
  name?: string;
  type?: string;
  panel?: string;
  unit?: string;
  points?: LegacyPoint[];
};

function stableId(prefix: string, index: number): string {
  return `${prefix}_${index + 1}`;
}

export function legacyIndicatorDataToChartSpec(
  indicatorData: LegacySeries[],
  signalData: LegacySeries[] = [],
): ChartSpec | null {
  if (!Array.isArray(indicatorData) || indicatorData.length === 0) return null;

  const rowsByTime = new Map<string | number, ChartRow>();
  const paneIds = new Map<string, string>();
  const series: ChartSeries[] = [];

  indicatorData.forEach((item, index) => {
    const points = Array.isArray(item.points) ? item.points : [];
    const id = stableId('series', index);
    const paneId = item.panel || 'primary_pane';
    paneIds.set(paneId, paneId);
    const type = item.type;
    const isCandlestick = type === 'ohlc' || type === 'candlestick';
    const dataKey = `value_${index + 1}`;
    const fieldMapping = {
      open: `open_${index + 1}`,
      high: `high_${index + 1}`,
      low: `low_${index + 1}`,
      close: `close_${index + 1}`,
    };

    points.forEach((point) => {
      if (point.time === undefined || point.time === null) return;
      const row = rowsByTime.get(point.time) || { time: point.time };
      if (isCandlestick) {
        row[fieldMapping.open] = Number(point.open);
        row[fieldMapping.high] = Number(point.high);
        row[fieldMapping.low] = Number(point.low);
        row[fieldMapping.close] = Number(point.close);
      } else {
        row[dataKey] = Number(point.value);
      }
      rowsByTime.set(point.time, row);
    });

    series.push({
      id,
      label: item.name || `Series ${index + 1}`,
      mark: isCandlestick ? 'candlestick' : type === 'bar' ? 'bar' : 'line',
      paneId,
      axisId: `${paneId}_axis`,
      dataKey: isCandlestick ? undefined : dataKey,
      fieldMapping: isCandlestick ? fieldMapping : undefined,
      style: {},
      unit: item.unit || 'value',
      frequency: 'provided',
      timezone: 'UTC',
      sourceIds: ['backtest-data'],
      showLastValue: true,
    });
  });

  const data = [...rowsByTime.values()].sort((left, right) => {
    const leftTime = new Date(String(left.time)).getTime();
    const rightTime = new Date(String(right.time)).getTime();
    if (Number.isFinite(leftTime) && Number.isFinite(rightTime)) {
      return leftTime - rightTime;
    }
    return String(left.time).localeCompare(String(right.time));
  });
  const panes = [...paneIds].map(([id]) => ({
    id,
    weight: id === 'primary_pane' ? 4 : 1,
    axisIds: [`${id}_axis`],
  }));
  const markers = signalData.flatMap((signal, signalIndex) =>
    (signal.points || [])
      .filter((point) => point.time !== undefined && point.time !== null)
      .map((point, pointIndex) => ({
        id: `signal_${signalIndex + 1}_${pointIndex + 1}`,
        paneId: series[0].paneId,
        seriesId: series[0].id,
        x: point.time as string | number,
        value:
          typeof point.value === 'number'
            ? point.value
            : typeof point.close === 'number'
              ? point.close
              : undefined,
        placement: 'at' as const,
        shape: 'circle' as const,
        color: '#F59E0B',
        label: signal.name || 'Signal',
      })),
  );

  return {
    kind: 'timeseries',
    title: 'Strategy indicators',
    xAxis: { key: 'time', type: 'time' },
    axes: panes.map((pane) => ({
      id: pane.axisIds[0],
      side: 'right',
      format: 'number',
    })),
    panes,
    series,
    data,
    referenceLines: [],
    markers,
  };
}
